import { DepressionContentPage } from "@/components/DepressionContentPage";
import type { FAQItem } from "@/components/DepressionContentPage";

const faqs: FAQItem[] = [
  {
    question: "Can trauma symptoms appear years later?",
    answer:
      "Yes. Trauma does not always remain part of daily awareness. A later experience can bring old feelings, fear, or hypervigilance back into focus, even years afterward.",
  },
  {
    question: "What is a trauma trigger?",
    answer:
      "A trigger is something that brings up feelings associated with a past experience. It can be a situation, sensation, or reminder, and it can feel confusing when the connection is not obvious.",
  },
  {
    question: "What does hypervigilance feel like?",
    answer:
      "Hypervigilance is a state of being constantly on alert. It can feel like always scanning for danger, difficulty relaxing, or being easily startled.",
  },
  {
    question: "Can trauma affect sleep?",
    answer:
      "Yes. Sleep disruption, racing thoughts, and difficulty feeling safe at night can accompany trauma-related symptoms.",
  },
  {
    question: "Can trauma and depression occur together?",
    answer:
      "Yes. Trauma, anxiety, sleep disruption, and depressive symptoms can overlap. An evaluation helps clarify the pattern and which options may be appropriate.",
  },
  {
    question: "How is PTSD evaluated?",
    answer:
      "A clinician reviews symptoms, triggers, sleep, mood, trauma history, functioning, and safety. These symptoms alone do not establish a diagnosis; an evaluation does.",
  },
  {
    question: "What treatment options may be considered?",
    answer:
      "Options may include trauma-focused psychotherapy, psychiatric care, medication management, and evidence-based neuromodulation when clinically appropriate. Treatment is individualized.",
  },
];

export default function PastTraumaResurfacing() {
  return (
    <DepressionContentPage
      seo={{
        title: "When Past Trauma Resurfaces",
        description:
          "Past trauma can sometimes become emotionally significant again years later. Learn about hypervigilance, anxiety, sleep and treatment evaluation.",
        canonical: "/when-past-trauma-resurfaces",
        keywords:
          "past trauma resurfacing, trauma symptoms years later, hypervigilance, trauma and sleep, PTSD evaluation",
      }}
      badge="Mental Health Education"
      heroTitle="Can Past Trauma Affect You Years Later?"
      heroSubtitle="Trauma does not always remain part of your daily awareness. Sometimes a later experience can bring old feelings, fear or hypervigilance back into focus."
      heroCtaPrimary="Check My Insurance Options"
      heroCtaPrimaryHref="/check-my-insurance-options"
      sections={[
        {
          heading: "Trauma Symptoms Do Not Look the Same for Everyone",
          variant: "cards",
          cards: [
            {
              title: "Hypervigilance",
              body: "Feeling constantly on alert or easily startled.",
            },
            {
              title: "Anxiety",
              body: "Persistent worry or unease that feels connected to the past.",
            },
            {
              title: "Avoidance",
              body: "Steering clear of situations, places, or reminders.",
            },
            {
              title: "Difficulty Feeling Safe",
              body: "A sense that danger is nearby even when it isn't.",
            },
            {
              title: "Sleep Disruption",
              body: "Trouble falling or staying asleep, or restless nights.",
            },
            {
              title: "Emotional Distress",
              body: "Strong feelings that seem disproportionate to the moment.",
            },
            {
              title: "Concentration Difficulties",
              body: "Trouble focusing when the mind feels preoccupied.",
            },
          ],
        },
        {
          heading: "Why a Trigger May Feel Confusing",
          variant: "text",
          body: (
            <p>
              A trigger is something that brings up feelings associated with a
              past experience. It can be a situation, a sensation, or a
              reminder, and the connection is not always obvious. When a
              response feels bigger than the moment, it can help to understand
              that triggers do not always announce themselves clearly.
            </p>
          ),
        },
        {
          heading: "Trauma, Sleep, Anxiety and Depression Can Overlap",
          variant: "text",
          body: (
            <p>
              Trauma-related symptoms rarely travel alone. Sleep disruption,
              anxiety, hypervigilance, and depressive symptoms can overlap,
              which is why an evaluation looks at the whole picture rather than
              a single symptom.
            </p>
          ),
        },
        {
          heading: "Treatment May Need to Address More Than One Layer",
          variant: "text",
          body: (
            <>
              <p>Depending on the individual, treatment options may include:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Trauma-focused psychotherapy</li>
                <li>Psychiatric care</li>
                <li>Medication management</li>
                <li>
                  Evidence-based neuromodulation or neurotechnology when
                  clinically appropriate
                </li>
              </ul>
              <p>
                TMS is not a universal treatment for PTSD. Whether
                neuromodulation is appropriate depends on clinical evaluation
                and individual circumstances.
              </p>
            </>
          ),
        },
        {
          heading: "Start With Understanding What's Happening",
          variant: "text",
          body: (
            <p>
              You do not need to have it all figured out before reaching out.
              Understanding what is happening is the first step toward
              determining which options may be appropriate.
            </p>
          ),
        },
      ]}
      faq={faqs}
      faqHeading="Frequently Asked Questions"
      relatedLinks={[
        {
          title: "When a Panic Attack Changes Everything",
          href: "/panic-anxiety-depression",
        },
        {
          title: "When Anxiety, Insomnia and Depression Overlap",
          href: "/depression-anxiety-insomnia",
        },
        {
          title: "Explore More Mental Health Education",
          href: "/mental-health-resources",
        },
      ]}
      finalCtaHeading="Understanding What's Resurfacing Starts With a Conversation"
      finalCtaText="If old feelings have come back into focus, an evaluation can help you understand what's happening and what options may be appropriate."
      finalCtaPrimaryLabel="Check My Insurance Options"
      finalCtaPrimaryHref="/check-my-insurance-options"
      finalCtaSecondaryLabel="Schedule a Private Consultation"
      finalCtaSecondaryHref="/contactus"
    />
  );
}
